// src/pages/Chat.jsx
import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../auth/api";
import { useAuth } from "../auth/AuthContext";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import BitacoraHistoryModal from "../components/BitacoraHistoryModal";

import DocumentosClienteModal from "../components/DocumentosClienteModal";
import SiniestralidadModal from "../components/SiniestralidadModal";

const MySwal = withReactContent(Swal);



// ===============================
// BASES para URLs absolutas (API/Storage)
// ===============================
const API_BASE = api?.defaults?.baseURL || "";

function computeBases() {
  if (/^https?:\/\//i.test(API_BASE)) {
    const u = new URL(API_BASE);
    return {
      ORIGIN: `${u.protocol}//${u.host}`,              // ej: http://localhost:3001
      API_PREFIX: u.pathname.replace(/\/+$/, ""),      // ej: "/api" o ""
    };
  }
  // baseURL relativa (ej: "/api"): mismo origin del frontend
  return {
    ORIGIN: window.location.origin,                    // ej: http://localhost:5173
    API_PREFIX: (API_BASE || "").replace(/\/+$/, ""),  // ej: "/api" o ""
  };
}
const { ORIGIN, API_PREFIX } = computeBases();

// ===============================
// WebSocket endpoint (auto http->ws + token)
// ===============================
const WS_URL = (() => {
  const base = `${ORIGIN}${API_PREFIX}`.replace(/\/+$/, "");
  const t = (() => {
    try {
      const h = api?.defaults?.headers?.common?.Authorization || "";
      const m = h.match(/^Bearer\s+(.+)$/i);
      if (m) return m[1];
      return localStorage.getItem("token") || "";
    } catch { return ""; }
  })();
  const qp = t ? `?t=${encodeURIComponent(t)}` : "";
  if (base.startsWith("https")) return base.replace("https", "wss") + "/ws-chat" + qp;
  if (base.startsWith("http")) return base.replace("http", "ws") + "/ws-chat" + qp;
  return "ws://localhost:3001/ws-chat" + qp;
})();


const STORAGE_BASE = `${ORIGIN}${API_PREFIX}/`;

// Obtiene el token (de axios o localStorage)
function getAuthToken() {
  const h = api?.defaults?.headers?.common?.Authorization || "";
  const m = h.match(/^Bearer\s+(.+)$/i);
  if (m) return m[1];
  try {
    const t = localStorage.getItem("token");
    if (t) return t;
  } catch { }
  return "";
}

// Devuelve URL firmada para <img>/<audio>
function signedUrl(relOrAbs) {
  if (!relOrAbs) return null;

  // Ya absoluta -> devolver igual
  if (/^https?:\/\//i.test(relOrAbs)) return relOrAbs;

  let rel = String(relOrAbs).replace(/\\/g, "/");

  // ✅ Asegura prefijo /storage/
  // Casos:
  // - "storage/avatars/1.jpg"  -> "/storage/avatars/1.jpg"
  // - "/storage/avatars/1.jpg" -> "/storage/avatars/1.jpg"
  // - "avatars/1.jpg"          -> "/storage/avatars/1.jpg"
  if (!rel.startsWith("/")) rel = "/" + rel;
  if (!rel.toLowerCase().startsWith("/storage/")) {
    // si venía como "/media/..." o "/avatars/..." lo mandamos a storage
    rel = "/storage" + rel;
  }

  const t = getAuthToken();
  const base = `${ORIGIN}${API_PREFIX}`.replace(/\/+$/, "");
  const url = `${base}${rel}`;
  return t ? `${url}?t=${encodeURIComponent(t)}` : url;
}

export default function Chat() {

  const [bitHistoryOpen, setBitHistoryOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [siniesOpen, setSiniesOpen] = useState(false);

  const [contractByContactId, setContractByContactId] = useState({});

  const [contact, setContact] = useState(null);
  const [contractNumber, setContractNumber] = useState("");
  const [savingContract, setSavingContract] = useState(false);

  // ---------------------------
  // Bitácora (Novedades SQL Server)
  // ---------------------------
  const [bitOpen, setBitOpen] = useState(false);
  const [bitText, setBitText] = useState("");
  const [bitSaving, setBitSaving] = useState(false);
  const [bitErr, setBitErr] = useState("");
  const [bitContestar, setBitContestar] = useState("0");

  // ---------------------------
  // Plantillas
  // ---------------------------
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");

  const { user } = useAuth();
  const navigate = useNavigate();

  // ---------------------------
  // Filtros (mantener estados, no UI)
  // ---------------------------
  const [accounts, setAccounts] = useState([]);
  const [waAccountId, setWaAccountId] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");

  // ---------------------------
  // Threads y mensajes
  // ---------------------------
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);

  // ---------------------------
  // Envío
  // ---------------------------
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  // ---------------------------
  // Metadatos del hilo
  // ---------------------------
  const [savingMeta, setSavingMeta] = useState(false);
  const [meta, setMeta] = useState({
    name: "",
    customer_type: "",
    budget: "",
    status: "",
    notes: "",
    assigned_to: null,
  });

  // Mini filtro de etapa en la vista de hilos
  const [stageFilter, setStageFilter] = useState("open"); // "open" | "working" | ""

  // Heurística ligera
  const stageOf = (t) => {
    const unread = Number(t.unread_count) || 0;
    const lastDir = String(t.last_direction || "").toLowerCase();
    return unread > 0 || lastDir === "in" ? "open" : "working";
  };

  // Excluir cerrados/ganados/perdidos
  const isClosedLike = (s = "") =>
    ["closed", "won", "lost", "cerrado", "ganado", "perdido"].some((k) =>
      s.includes(k)
    );

  const isOpenWorkingLead = (t) => {
    const v = String(
      t.lead_status ?? t.leadStatus ?? t.lead?.status ?? t.status ?? ""
    ).toLowerCase();
    return v ? !isClosedLike(v) : true;
  };

  // Lista mostrada según el mini filtro
  const viewThreads = useMemo(() => {
    let rows = (threads || []).slice();
    if (stageFilter) {
      rows = rows.filter((t) => stageOf(t) === stageFilter || (selected && t.id === selected.id));
    }
    // Asegurar que si hay un seleccionado, siempre aparezca en la lista
    if (selected && !rows.some((t) => t.id === selected.id)) {
      rows.unshift(selected);
    }
    return rows.sort(
      (a, b) =>
        (b.unread_count || 0) - (a.unread_count || 0) ||
        new Date(b.last_at || b.created_at) - new Date(a.last_at || a.created_at)
    );
  }, [threads, stageFilter, selected]);

  // ---------------------------
  // Lead
  // ---------------------------
  const [lead, setLead] = useState(null);
  const [closingLead, setClosingLead] = useState(false);

  const [leadPhoneFix, setLeadPhoneFix] = useState("");
  const [savingPhoneFix, setSavingPhoneFix] = useState(false);


  // Drawer detalles
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Contadores de leads
  const [leadCounters, setLeadCounters] = useState({
    open: 0,
    working: 0,
    won: 0,
    lost: 0,
    closed: 0,
    new_24h: 0,
  });

  // Usuarios por cuenta
  const [accountUsers, setAccountUsers] = useState([]);

  // Refs de scroll
  const messagesRef = useRef(null);
  const stickRef = useRef(true);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const STICK_THRESHOLD = 120;

  // Preselección por query (?phone=&wa=&threadId=&leadId=)
  const preselect = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return {
      phone: sp.get("phone") || "",
      wa: sp.get("wa") || "",
      threadId: sp.get("threadId") || sp.get("thread_id") || "",
      leadId: sp.get("leadId") || sp.get("lead_id") || "",
    };
  }, []);

  // Normaliza teléfono respetando identificadores LID (LID:xxxxx)
  const normalizePhoneFrontend = (raw) => {
    const s = String(raw || "").trim();
    if (s.startsWith("LID:")) return s;
    return s.replace(/\D/g, "");
  };

  const ensureThreadForPhone = async (phone, name = null) => {
    if (!phone) return null;
    const isLid = String(phone).startsWith("LID:");
    const cleanPhone = isLid ? String(phone).trim() : normalizePhoneFrontend(phone);

    try {
      // 1) Buscar si ya existe un hilo para este teléfono (LID o número normal)
      const { data } = await api.get("/chat/threads", {
        params: { q: cleanPhone },
      });

      const rows = data?.rows || [];
      if (rows.length) {
        if (preselect.wa) {
          const exactWa = rows.find(
            (r) =>
              (String(r.phone) === cleanPhone || (!isLid && String(r.phone).replace(/\D/g, "") === cleanPhone)) &&
              String(r.wa_account_id) === String(preselect.wa)
          );
          if (exactWa) return exactWa;
        }

        const exact = rows.find(
          (r) =>
            String(r.phone) === cleanPhone ||
            (!isLid && String(r.phone).replace(/\D/g, "") === cleanPhone)
        );
        return exact || rows[0];
      }

      // 2) Si no es LID y no existe hilo, crearlo
      if (!isLid) {
        const { data: createdData } = await api.post(`/contacts/${encodeURIComponent(cleanPhone)}/thread`, {
          name: name || null,
          created_by: user?.id || null,
          wa_account_id: preselect.wa || waAccountId || null,
        });

        if (createdData?.thread) return createdData.thread;

        const res2 = await api.get("/chat/threads", {
          params: { q: cleanPhone },
        });

        const rows2 = res2?.data?.rows || [];
        if (!rows2.length) return null;

        const exact2 = rows2.find(
          (r) => String(r.phone).replace(/\D/g, "") === cleanPhone
        );

        return exact2 || rows2[0];
      }

      return null;
    } catch (e) {
      console.error("ensureThreadForPhone", e?.response?.data || e?.message || e);
      return null;
    }
  };

  const saveLeadPhoneFix = async () => {
    if (!lead?.id) {
      MySwal.fire({ icon: "info", title: "Sin lead", text: "Primero inicia o selecciona un lead." });
      return;
    }

    const clean = String(leadPhoneFix || "").replace(/\D/g, "");
    if (!clean || clean.length < 10 || clean.length > 15) {
      MySwal.fire({
        icon: "warning",
        title: "Teléfono inválido",
        text: "Escribe el teléfono en formato E.164 (solo dígitos), ej: 5939XXXXXXXX",
      });
      return;
    }

    setSavingPhoneFix(true);
    try {
      // endpoint que vamos a crear en backend
      const { data } = await api.patch(`/leads/${lead.id}/phone`, { phone: clean });

      const updated = data?.lead || null;
      setLead(updated);
      setLeadPhoneFix(updated?.phone_override || "");

      MySwal.fire({
        icon: "success",
        title: "Teléfono actualizado",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (e) {
      MySwal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text: e?.response?.data?.error || e?.message || "Error",
      });
    } finally {
      setSavingPhoneFix(false);
    }
  };

  const claimIfUnassigned = async (thread) => {
    if (!thread?.id) return;
    try {
      const { data } = await api.post(`/chat/threads/${thread.id}/claim`);
      if (data?.claimed) {
        setMeta((m) => ({ ...m, assigned_to: Number(data.assigned_to) }));
        setSelected((s) => (s ? { ...s, assigned_to: Number(data.assigned_to) } : s));

        MySwal.fire({
          icon: "success",
          title: "Lead asignado",
          text: "Este lead se te asignó automáticamente.",
          timer: 1800,
          showConfirmButton: false,
        });
      }
    } catch (e) {
      console.error("claimIfUnassigned", e?.response?.data || e?.message || e);
    }
  };

  useEffect(() => {
    (async () => {
      // 1) Si viene con ID de hilo explícito
      if (preselect.threadId) {
        try {
          const { data } = await api.get(`/chat/threads/${preselect.threadId}`);
          if (data?.thread) {
            setSelected(data.thread);
            if (data.thread.wa_account_id) setWaAccountId(String(data.thread.wa_account_id));
            return;
          }
        } catch (e) {
          console.error("error cargando threadId preseleccionado", e);
        }
      }

      // 2) Si viene con ID de lead explícito
      if (preselect.leadId) {
        try {
          const { data: leadData } = await api.get(`/leads/${preselect.leadId}`);
          const l = leadData?.lead;
          if (l?.thread_id) {
            const { data: tData } = await api.get(`/chat/threads/${l.thread_id}`);
            if (tData?.thread) {
              setSelected(tData.thread);
              if (tData.thread.wa_account_id) setWaAccountId(String(tData.thread.wa_account_id));
              return;
            }
          }
        } catch (e) {
          console.error("error resolviendo leadId preseleccionado", e);
        }
      }

      // 3) Si viene con teléfono
      if (preselect.phone) {
        const found = await ensureThreadForPhone(preselect.phone, null);
        if (found) {
          setSelected(found);
          if (found.wa_account_id) setWaAccountId(String(found.wa_account_id));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselect.phone, preselect.threadId, preselect.leadId]);

  // Mapa de cuentas
  const accountMap = useMemo(() => {
    const m = {};
    for (const a of accounts) m[a.id] = a;
    return m;
  }, [accounts]);

  // Totales
  const unreadTotal = useMemo(
    () => (threads || []).reduce((acc, t) => acc + (Number(t.unread_count) || 0), 0),
    [threads]
  );
  const fmtCount = (n) => (typeof n === "number" && n > 99 ? "99+" : n || 0);
  // Detecta si el string trae TZ (Z o +hh:mm / -hhmm)
  const hasTZ = (s) => {
    const v = String(s || "").trim();
    return /z$/i.test(v) || /[+-]\d{2}:?\d{2}$/.test(v);
  };

  // Parsea fechas de SQLite/local SIN convertir (no TZ) y UTC (con TZ) con conversión a local automática
  const parseDateFlex = (input) => {
    if (!input) return null;

    const s = String(input).trim();

    // Caso 1: ISO con TZ -> Date lo entiende (UTC/offset) y lo convierte al mostrar con toLocale*
    if (hasTZ(s)) {
      const d = new Date(s);
      return isNaN(d) ? null : d;
    }

    // Caso 2: SQLite / local: "YYYY-MM-DD HH:MM:SS" o "YYYY-MM-DDTHH:MM:SS"
    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
    );
    if (m) {
      const [, Y, Mo, D, h, mi, se] = m;
      const d = new Date(
        Number(Y),
        Number(Mo) - 1,
        Number(D),
        Number(h),
        Number(mi),
        Number(se || 0)
      );
      return isNaN(d) ? null : d;
    }

    // Fallback
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };

  // Normaliza SOLO para UI: si viene sin TZ lo deja "local-like" (sin Z)
  const normalizeNoTZToLocalLike = (input) => {
    if (!input) return null;
    const s = String(input).trim();
    if (hasTZ(s)) return s; // no tocar

    const m = s.match(
      /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::(\d{2}))?/
    );
    if (m) {
      const date = m[1];
      const hhmm = m[2];
      const ss = m[3] ?? "00";
      return `${date}T${hhmm}:${ss}`; // sin Z
    }

    return s;
  };
  // Utils de formato
  const fmtTime = (v) => {
    if (!v) return "";
    const d = parseDateFlex(v);
    if (!d) return "";

    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };
  const sameDay = (a, b) => {
    const da = parseDateFlex(a);
    const db = parseDateFlex(b);
    if (!da || !db) return false;

    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  };

  const dayLabel = (v) => {
    const d = parseDateFlex(v);
    if (!d) return "";

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (sameDay(d, today)) return "Hoy";
    if (sameDay(d, yesterday)) return "Ayer";
    return d.toLocaleDateString();
  };
  const statusMark = (wa) => {
    if (!wa) return "";
    const s = String(wa).toLowerCase();
    if (s === "sending") return "…";
    if (s === "sent") return "✓";
    if (s === "delivered" || s === "read") return "✓✓";
    if (s === "failed" || s === "error") return "✗";
    return "";
  };
  const initials = (name = "", phone = "") => {
    const n = (name || "").trim();
    if (n) {
      const parts = n.split(/\s+/).filter(Boolean);
      return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
    }
    const s = String(phone);
    return s.slice(-2);
  };
  const leadStatusColor = (st = "") => {
    const s = String(st || "").toLowerCase();
    if (s === "open") return "#60a5fa";
    if (s === "working") return "#f59e0b";
    if (s === "won") return "#22c55e";
    if (s === "lost") return "#ef4444";
    if (s === "closed") return "#94a3b8";
    return "#94a3b8";
  };

  // ==================================
  // Data fetchers
  // ==================================
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { data } = await api.get("/messages/templates", {
        params: { q: templateSearch }
      });
      setTemplates(data.rows || []);
    } catch (e) {
      console.error("fetchTemplates", e?.response?.data || e?.message || e);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get("/wa/accounts");
      setAccounts(data.rows || []);
      if (selected?.wa_account_id) {
        setWaAccountId(String(selected.wa_account_id));
        fetchUsersForAccount(selected.wa_account_id);
      }
    } catch (e) {
      console.error("fetchAccounts", e?.response?.data || e?.message || e);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const { data } = await api.get(`/chat/threads/${threadId}/messages`);
      const raw = Array.isArray(data?.messages)
        ? data.messages
        : Array.isArray(data?.rows)
          ? data.rows
          : [];
      const normalized = raw
        .map((m) => {
          const text =
            m.text ??
            m.body ??
            m.message ??
            m.content ??
            (m.type === "image" ? "[Imagen]" : "");
          const createdRaw = m.created_at ?? m.timestamp ?? m.date ?? m.createdAt ?? null;
          const created = normalizeNoTZToLocalLike(createdRaw) || createdRaw;
          const direction =
            m.direction ??
            (typeof m.from_me === "boolean" ? (m.from_me ? "out" : "in") : null);

          // ---- MEDIA ----
          let media_kind = m.media_kind || m.type || null;
          if (!media_kind && m.media_mime) {
            if (m.media_mime.startsWith("image/")) media_kind = "image";
            if (m.media_mime === "application/pdf") media_kind = "pdf";
          }
          const media_mime = m.media_mime || m.mime || null;
          if (!media_kind && typeof media_mime === "string") {
            if (media_mime.startsWith("image/")) media_kind = "image";
            else if (media_mime.startsWith("audio/")) media_kind = "audio";
          }

          const rawUrl = m.media_url || "";
          const cleanPath = (m.media_path || "")
            .replace(/^storage[\\/]/i, "")
            .replace(/\\/g, "/");

          let media_url = null;
          if (rawUrl) {
            media_url = /^https?:\/\//i.test(rawUrl) ? rawUrl : signedUrl(rawUrl);
          } else if (cleanPath) {
            media_url = signedUrl(cleanPath);
          }

          const duration_ms =
            m.duration_ms ?? (m.duration ? Number(m.duration) * 1000 : null);

          return {
            id: m.wa_message_id ?? m.id ?? `tmp_${Math.random()}`,
            user_id: m.user_id ?? null,
            user_name: m.user_name ?? null,
            direction: direction ?? "in",
            text,
            wa_message_id: m.wa_message_id ?? null,
            wa_status: m.wa_status ?? m.status ?? null,
            created_at: created || new Date().toISOString(),
            media_kind,
            media_url,
            duration_ms,
            media_mime: media_mime ?? null,
          };
        })
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      // Quita duplicados/ruido
      const seen = new Set();
      const filtered = [];
      for (const m of normalized) {
        const key = m.wa_message_id || m.id || `${m.direction}|${m.text}|${m.created_at}`;
        if (seen.has(key)) continue;
        seen.add(key);
        //if (m.direction === "out" && !String(m.text || "").trim()) continue;
        if (!m.text && !m.media_kind && !m.media_url) continue;
        filtered.push(m);
      }
      setMessages(filtered);
    } catch (e) {
      if (e?.response?.status === 403) {
        alert("No tienes permiso para ver los mensajes de este hilo.");
      } else {
        console.error("fetchMessages", e?.response?.data || e?.message || e);
      }
    }
  };

  const fetchLeadForSelected = async (t) => {
    if (!t) return setLead(null);
    try {
      const { data } = await api.get("/leads", {
        params: { q: t.phone, wa_account_id: t.wa_account_id || "", status: "" },
      });
      const rows = data?.rows || [];
      let current = null;
      if (t.lead_id) current = rows.find((r) => r.id === t.lead_id) || null;
      if (!current && rows.length) current = rows[0];
      const ld = current || null;
      setLead(ld);
      setLeadPhoneFix(ld?.phone_override || "");
    } catch (e) {
      console.error("fetchLead", e?.response?.data || e?.message || e);
      setLead(null);
    }
  };

  const fetchLeadCounters = async () => {
    try {
      const { data } = await api.get("/leads/counters");
      setLeadCounters(data?.counts || {});
    } catch {
      /* silencioso */
    }
  };

  const fetchUsersForAccount = async (accId) => {
    if (!accId) {
      setAccountUsers([]);
      return;
    }
    try {
      const { data } = await api.get(`/accounts/${accId}/users`);
      setAccountUsers(data.users || []);
    } catch (e) {
      console.error("fetchUsersForAccount", e);
      setAccountUsers([]);
    }
  };

  const fetchThreads = async () => {
    setLoadingThreads(true);
    try {
      const { data } = await api.get("/chat/threads", {
        params: {
          q: q || "",
          assignedTo: assignedTo || "",
          status: statusFilter || "",
          wa_account_id: waAccountId || "",
        },
      });
      const rows = data?.rows || [];
      setThreads(rows);
      return rows;
    } catch (e) {
      console.error("fetchThreads", e?.response?.data || e?.message || e);
      return [];
    } finally {
      setLoadingThreads(false);
    }
  };

  useEffect(() => {
    fetchThreads();
  }, [waAccountId, assignedTo, statusFilter, q]);

  useEffect(() => {
    fetchAccounts();
    fetchLeadCounters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const msgKey = (m) =>
    m.client_msg_id ||
    m.wa_message_id ||
    m.id ||
    `${m.direction}|${(m.text || "").slice(0, 120)}|${m.created_at}`;

  function upsertByClientIdOrWaId(incoming) {
    setMessages((prev) => {
      const ck = incoming.client_msg_id;
      const wk = incoming.wa_message_id;

      let found = false;

      const next = prev.map((m) => {
        // 1) Primero intenta matchear por client_msg_id (optimista)
        if (ck && m.client_msg_id && m.client_msg_id === ck) {
          found = true;
          return { ...m, ...incoming };
        }

        // 2) Si no hay client id, matchea por wa_message_id
        if (!ck && wk && m.wa_message_id && m.wa_message_id === wk) {
          found = true;
          return { ...m, ...incoming };
        }

        return m;
      });

      return found ? next : [...prev, incoming];
    });
  }

  function pushIfNew(next) {
    setMessages(prev => {
      const has = prev.some(x => msgKey(x) === msgKey(next));
      return has ? prev : [...prev, next];
    });
  }

  function upsertByWaId(incoming) {
    setMessages(prev => {
      const k = incoming.wa_message_id;
      if (!k) return prev;
      let found = false;
      const next = prev.map(m => {
        if ((m.wa_message_id && m.wa_message_id === k) || (!m.wa_message_id && m.id === incoming.id)) {
          found = true;
          return { ...m, ...incoming };
        }
        return m;
      });
      return found ? next : [...prev, incoming];
    });
  }

  /**
   * Bitacoras
   * @param {*} limit 
   * @returns 
   */

  function buildBitacoraFromChat(limit = 12) {
    const rows = (messages || []).slice(-limit);
    const header = [
      `Cliente: ${selected?.name || selected?.phone || ""}`,
      `Teléfono: ${effectivePhone || ""}`,
      `Contrato: ${contractNumber || ""}`,
      `Usuario: ${user?.name || user?.username || ""}`,
      `Fecha: ${new Date().toLocaleString()}`,
      "----------------------------------------",
    ].filter(Boolean).join("\n");

    const body = rows.map((m) => {
      const who = m.direction === "out"
        ? (m.user_name ? `Tú (${m.user_name})` : "Tú")
        : (selected?.name || selected?.phone || "Cliente");

      const t = fmtTime(m.created_at);
      const text = (m.text || "").trim();
      const media = m.media_kind && m.media_url ? ` [${m.media_kind}]` : "";

      return `${t} · ${who}: ${text || media}`.trim();
    }).join("\n");

    return `${header}\n${body}`.trim();
  }

  async function saveBitacora() {
    setBitErr("");

    const contrato = String(contractNumber || "").trim();
    if (!contrato) {
      setBitErr("Este contacto no tiene número de contrato.");
      return;
    }

    const novedad = String(bitText || "").trim();
    if (!novedad) {
      setBitErr("Escribe la bitácora antes de guardar.");
      return;
    }

    setBitSaving(true);
    try {
      const usuario = user?.username || user?.name || "";

      await api.post(`/contratos/${encodeURIComponent(contrato)}/novedades`, {
        novedad,
        usuario,
        origen: "CT",
        contestar: Number(bitContestar),
        urgencia: false,
      });

      MySwal.fire({
        icon: "success",
        title: "Bitácora guardada",
        timer: 1300,
        showConfirmButton: false,
      });

      setBitOpen(false);
      setBitText("");
      setBitContestar("0");
    } catch (e) {
      setBitErr(e?.response?.data?.error || e?.message || "No se pudo guardar");
    } finally {
      setBitSaving(false);
    }
  }

  function normalizeIn(m) {
    const id = m.wa_message_id || m.id || `tmp_${Date.now()}`;

    const rawCreated = m.at || m.timestamp || m.created_at || new Date().toISOString();

    // ✅ Si NO trae TZ, lo dejamos tal cual (solo lo pasamos a "YYYY-MM-DDTHH:MM:SS")
    // ✅ Si trae TZ, lo dejamos (para que se convierta a local al mostrar)
    const created_at = normalizeNoTZToLocalLike(rawCreated) || rawCreated;

    const raw = m.media_url || m.media_path || null;
    const media_url = raw ? signedUrl(String(raw).replace(/\\/g, "/")) : null;

    let media_kind = m.media_kind || null;
    if (!media_kind && m.media_mime) {
      if (m.media_mime.startsWith("image/")) media_kind = "image";
      else if (m.media_mime === "application/pdf") media_kind = "pdf";
      else if (m.media_mime.startsWith("audio/")) media_kind = "audio";
      else media_kind = "file";
    }

    return {
      id,
      client_msg_id: m.client_msg_id || null,
      wa_message_id: m.wa_message_id || null,
      direction: m.direction ?? (m.from_me ? "out" : "in"),
      text: m.text || m.body || "",
      created_at, // ✅ sin toISOString()
      media_kind,
      media_url,
      wa_status: m.status || m.wa_status || null,
      user_name: m.user_name || null,
    };
  }


  // ==================================
  // Efectos por selección de hilo
  // ==================================
  useEffect(() => {
    if (!selected) return;

    setMessages([]);
    fetchMessages(selected.id);
    fetchLeadForSelected(selected);

    (async () => {
      try {
        const th = selected;
        const contactId = th?.contact_id;
        if (!contactId) {
          setContact(null);
          setContractNumber("");
          return;
        }
        const { data } = await api.get(`/contacts/id/${contactId}`);
        const c = data?.contact || null;
        setContact(c);
        setContractNumber(c?.contract_number || "");

        // guarda en cache para pintarlo en la lista izquierda
        if (c?.id) {
          setContractByContactId((prev) => ({
            ...prev,
            [String(c.id)]: c.contract_number || "",
          }));
        }

      } catch (e) {
        console.error("fetchContact", e?.response?.data || e?.message || e);
        setContact(null);
        setContractNumber("");
      }
    })();

    setMeta({
      name: selected.name || "",
      customer_type: selected.customer_type || "",
      budget: selected.budget || "",
      status: selected.status || "",
      notes: selected.notes || "",
      assigned_to: selected.assigned_to ?? null,
    });

    claimIfUnassigned(selected);

    if (selected?.wa_account_id) fetchUsersForAccount(selected.wa_account_id);
    if (selected.wa_account_id && !waAccountId) setWaAccountId(String(selected.wa_account_id));

    stickRef.current = true;
    setShowScrollBottom(false);
    setTimeout(() => {
      const el = messagesRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  // ===============================
  // Realtime por WebSocket (sin polling)
  // ===============================
  useEffect(() => {
    if (!selected) return;

    const ws = new WebSocket(WS_URL);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);

        if (data?.type === "new_message") {
          const m = data.payload || {};
          if (String(m.thread_id) !== String(selected.id)) return;
          console.log("[WS raw]", data);
          const n = normalizeIn(m);

          // ✅ FILTRO anti-basura (NO renderizar burbujas vacías)
          const hasText = String(n.text || "").trim().length > 0;
          const hasMedia = !!(n.media_url || n.media_kind);

          // si no trae texto ni media → ignorar
          if (!hasText && !hasMedia) return;

          // si no trae identificador y tampoco contenido → ignorar
          if (!n.wa_message_id && !n.client_msg_id && !hasText && !hasMedia) return;

          upsertByClientIdOrWaId(n);

          try {
            if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
          } catch { }
        }

      } catch (err) {
        console.error("WS parse error", err);
      }
    };
    ws.onerror = (e) => console.error("WS error", e);
    ws.onclose = () => { };

    return () => {
      try { ws.close(); } catch { }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  // autoscroll
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onMessagesScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const sticky = distanceFromBottom < STICK_THRESHOLD;
    stickRef.current = sticky;
    setShowScrollBottom(!sticky);
  };

  // ---------------------------
  // Actions
  // ---------------------------
  const openTemplateModal = () => {
    setShowTemplates(true);
    fetchTemplates();
  };

  const saveContractNumber = async () => {
    if (!contact?.id) {
      MySwal.fire({ icon: "info", title: "Sin contacto", text: "Este hilo no tiene contacto asociado." });
      return;
    }

    const val = String(contractNumber || "").trim();

    setSavingContract(true);
    try {
      const { data } = await api.patch(`/contacts/${contact.id}`, {
        contract_number: val || null,
      });
      const updated = data?.contact || null;
      setContact(updated);
      setContractNumber(updated?.contract_number || "");

      MySwal.fire({
        icon: "success",
        title: "Contrato guardado",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      MySwal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text: e?.response?.data?.error || e?.message || "Error",
      });
    } finally {
      setSavingContract(false);
    }
  };

  const onSend = async (e) => {
    if (e) e.preventDefault();
    const body = (text || "").trim();
    if (!body || !selected || sending) return;

    const client_msg_id = `c_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    setSending(true);
    try {
      const optimistic = {
        id: `tmp_${Date.now()}`,
        client_msg_id,              // ✅ CLAVE
        user_id: user?.id || null,
        direction: "out",
        text: body,
        wa_message_id: null,
        wa_status: "sending",
        created_at: new Date().toISOString(),
        user_name: user?.name || null,
      };

      setMessages((m) => [...m, optimistic]);
      stickRef.current = true;

      await api.post(`/chat/threads/${selected.id}/send`, {
        text: body,
        client_msg_id,
      });

      setText("");
      // WS actualizará UI
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo enviar");
      fetchMessages(selected.id);
    } finally {
      setSending(false);
    }
  };


  const onSendTemplate = (tpl) => {
    if (!tpl) return;
    setText(tpl.content || tpl.body || tpl.text || "");
    setShowTemplates(false);
    setTimeout(() => {
      const ta = document.querySelector("textarea");
      if (ta) ta.focus();
    }, 100);
  };

  const onSaveMeta = async () => {
    if (!selected) return;
    setSavingMeta(true);
    try {
      const { data } = await api.patch(`/chat/threads/${selected.id}`, meta);
      setSelected(data.thread);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo guardar");
    } finally {
      setSavingMeta(false);
    }
  };

  const onChangeChannel = async (newAccountId) => {
    if (!selected || !newAccountId) return;
    try {
      await api.patch(`/chat/threads/${selected.id}/channel`, {
        wa_account_id: Number(newAccountId),
        reason: "Cambio manual desde UI",
      });
      const next = { ...selected, wa_account_id: Number(newAccountId) };
      setSelected(next);
      fetchLeadForSelected(next);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo cambiar el canal");
    }
  };

  const onLeadStart = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/chat/threads/${selected.id}/lead/start`);
      setLead(data.lead || null);
      fetchLeadCounters();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo iniciar el lead");
    }
  };

  const onLeadClose = async (outcome = "closed") => {
    if (!selected) return;
    setClosingLead(true);
    try {
      const { data } = await api.post(`/chat/threads/${selected.id}/lead/close`, {
        outcome,
        notes: (meta.notes || "").trim(),
      });
      if (data?.lead) setLead(data.lead);
      await fetchLeadForSelected(selected);
      await fetchLeadCounters();
    } catch (e) {
      const msg = e?.response?.data?.error || "No se pudo cerrar el lead";
      alert(msg);
    } finally {
      setClosingLead(false);
    }
  };

  const markRead = async () => {
    if (!selected) return;
    try {
      await api.post(`/chat/threads/${selected.id}/read`);
    } catch (e) {
      console.error(e);
    }
  };

  // ---------------------------
  // Render helpers
  // ---------------------------
  const threadItem = (t) => {
    const acc = accountMap[t.wa_account_id] || {};
    const subtitleLeft =
      (t.last_direction === "in" ? "🡐" : "🡒") + " " + (t.last_message || "");
    const subtitleRight = (t.last_at || t.created_at || "")
      .replace("T", " ")
      .slice(0, 19);
    const selectedCls = selected?.id === t.id ? "selected" : "";

    const contrato = t?.contact_id
      ? (contractByContactId[String(t.contact_id)] || "")
      : "";

    return (
      <div key={t.id} onClick={() => setSelected(t)} className={`thread-item ${selectedCls}`}>
        <div className="thread-top">
          <div className="thread-avatar">{initials(t.name, t.phone)}</div>
          <div className="thread-meta">
            <div className="thread-name">{t.name || t.phone}</div>
            <div className="thread-sub">
              <span className="tag">{acc.label || "—"}</span>
              {contrato ? (
                <>
                  <span className="sep">·</span>
                  <span className="tag contract">CTR {contrato}</span>
                </>
              ) : null}

              <span className="sep">·</span>
              <span className="muted">{t.phone}</span>
            </div>
          </div>

          <div className="thread-right">
            <span className={`status-pill ${stageOf(t)}`}>{stageOf(t)}</span>
            {t.unread_count > 0 && <span className="badge">{t.unread_count}</span>}
          </div>
        </div>
        <div className="thread-bottom">
          <span className="ellipsis">{subtitleLeft}</span>
          <span className="muted">{subtitleRight}</span>
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (!selected) return <div className="muted">Elige un hilo a la izquierda.</div>;
    if (messages.length === 0) return <div className="muted">Aún no hay mensajes.</div>;

    let lastDate = "";
    const blocks = [];

    for (const m of messages) {
      const dateStr = (m.created_at || "").slice(0, 10);
      if (dateStr && dateStr !== lastDate) {
        blocks.push(
          <div className="date-sep" key={`sep_${dateStr}_${Math.random()}`}>
            {dayLabel(m.created_at)}
          </div>
        );
        lastDate = dateStr;
      }
      const isOut = m.direction === "out";
      const who = isOut
        ? m.user_name
          ? `Tú (${m.user_name})`
          : user?.name
            ? `Tú (${user.name})`
            : "Tú"
        : selected?.name || selected?.phone || "Cliente";

      const allowed = new Set(["sending", "sent", "delivered", "read", "failed", "error"]);
      const status = (m.wa_status || "").toLowerCase();
      const statusText = isOut && allowed.has(status) ? ` · ${statusMark(status)}` : "";

      blocks.push(
        <div key={m.id} className={`msg-row ${isOut ? "right" : "left"}`}>
          <div className={`bubble ${isOut ? "out" : "in"}`}>
            <div className="bubble-body">
              {m.media_kind === "image" && m.media_url && (
                <img src={m.media_url} alt="imagen" className="bubble-img" />
              )}

              {m.media_kind === "pdf" && m.media_url && (
                <a
                  href={m.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bubble-pdf"
                >
                  📄 Ver PDF
                </a>
              )}
              {m.media_kind === "file" && m.media_url && (
                <a
                  href={m.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bubble-file"
                >
                  📎 Descargar archivo
                </a>
              )}

              {m.media_kind === "audio" && m.media_url && (
                <audio controls src={m.media_url} className="bubble-audio" />
              )}
              {m.text ? <div className="bubble-text">{m.text}</div> : null}
            </div>
            <div className="msg-meta">
              {who} · {fmtTime(m.created_at)}
              {statusText}
            </div>
          </div>
        </div>
      );
    }
    return blocks;
  };

  const LeadCountersMini = () => {
    const c = leadCounters || {};
    return (
      <div className="lead-counters mini">
        <span className="pill open">
          Open <b>{fmtCount(c.open)}</b>
        </span>
        <span className="pill working">
          Working <b>{fmtCount(c.working)}</b>
        </span>
        <span className={`new24 ${c.new_24h > 0 ? "pulse" : ""}`} title="Nuevos en 24h">
          <svg viewBox="0 0 24 24">
            <path
              d="M10 21h4m7-4c-1.1 0-2-.9-2-2V9a7 7 0 10-14 0v6c0 1.1-.9 2-2 2h18z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
          {fmtCount(c.new_24h)}
        </span>
      </div>
    );
  };
  const effectivePhone = (lead?.phone_override || lead?.phone || selected?.phone || "");
  return (
    <div className="chat-root">
      <style>{css}</style>

      {/* Barra superior compacta */}
      <div className="topbar">
        <div className="left-actions">
          <button className="btn back" onClick={() => navigate("/leads")} title="Volver a Leads">
            ← Leads
          </button>
        </div>

        <div className="right-info" style={{ marginLeft: "auto" }}>
          <div className="unread">Sin leer: <b>{fmtCount(unreadTotal)}</b></div>
          <LeadCountersMini />
        </div>
      </div>

      <div className={`chat-grid left-collapsed`}>
        {/* Centro */}
        <div className="card center-pane">
          <div className="section header-row">
            <div className="header-left">
              <div className="title-row">
                <div className="title">
                  {selected ? (selected.name || effectivePhone) : "Selecciona una conversación"}
                </div>

                {selected && contractNumber ? (
                  <span className="contract-pill" title="Número de contrato">
                    CTR {contractNumber}
                  </span>
                ) : null}
              </div>

              {selected && (
                <div className="muted small">
                  {effectivePhone}
                  {selected?.wa_account_id && accountMap[selected.wa_account_id]
                    ? ` · ${accountMap[selected.wa_account_id].label} (${accountMap[selected.wa_account_id].provider})`
                    : ""}
                </div>
              )}
            </div>

            <div className="header-actions">
              <button className="icon-btn" onClick={markRead} title="Marcar leído" aria-label="Marcar leído">
                <svg viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>

              <div className="select-wrap small">
                <select
                  className="input select"
                  value={selected?.wa_account_id || ""}
                  onChange={(e) => onChangeChannel(e.target.value)}
                  title="Cambiar canal del hilo"
                  disabled
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label} ({a.provider})
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-bitacora"
                onClick={() => {
                  setBitErr("");
                  setBitOpen(true);
                  setBitOpen(true);
                  // opcional:
                  // setBitText(buildBitacoraFromChat(12));
                }}
                title="Crear bitácora (guardar en SQL Server)"
              >
                📝 Crear bitácora
              </button>

              <button
                type="button"
                className="btn"
                onClick={() => setBitHistoryOpen(true)}
                disabled={!contractNumber}
                title={!contractNumber ? "Este contacto no tiene contrato" : "Ver historial"}
              >
                📚 Historial Bitácora
              </button>

              <button
                type="button"
                className="btn"
                onClick={() => setDocsOpen(true)}
                title="Ver documentos del cliente"
              >
                📄 Deudas del cliente
              </button>

              <button
                type="button"
                className="btn"
                onClick={() => setSiniesOpen(true)}
                disabled={!contractNumber}
                title={!contractNumber ? "Este contacto no tiene contrato" : "Ver siniestralidad"}
              >
                🏥 Siniestralidad
              </button>

              <button
                className={`icon-btn ${detailsOpen ? "active" : ""}`}
                onClick={() => setDetailsOpen((v) => !v)}
                title="Mostrar detalles (ℹ️)"
                aria-label="Mostrar detalles"
              >
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M12 10v6M12 7.5v.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div ref={messagesRef} onScroll={onMessagesScroll} className="messages">
            {renderMessages()}
            {showScrollBottom && (
              <button
                className="scroll-bottom"
                onClick={() => {
                  const el = messagesRef.current;
                  if (el) el.scrollTop = el.scrollHeight;
                  stickRef.current = true;
                  setShowScrollBottom(false);
                }}
              >
                ↓
              </button>
            )}
          </div>

          <form onSubmit={onSend} className="section input-bar">
            <textarea
              className="input"
              placeholder="Escribe un mensaje…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onInput={(e) => {
                e.currentTarget.style.height = "40px";
                e.currentTarget.style.height =
                  Math.min(e.currentTarget.scrollHeight, 160) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
              rows={1}
              disabled={!selected || sending}
              style={{ resize: "none", minHeight: 40, maxHeight: 160 }}
            />
            <input
              type="file"
              accept="image/*,application/pdf"
              style={{ display: "none" }}
              id="fileInput"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !selected) return;

                const client_msg_id = `c_${Date.now()}_${Math.random().toString(16).slice(2)}`;

                // tipo
                const mediaMime = file.type;
                let mediaType = "file";
                if (mediaMime.startsWith("image/")) mediaType = "image";
                else if (mediaMime === "application/pdf") mediaType = "pdf";

                // ✅ optimista: preview instantáneo
                const optimistic = {
                  id: `tmp_${Date.now()}`,
                  client_msg_id,
                  direction: "out",
                  text: "",
                  wa_message_id: null,
                  wa_status: "sending",
                  created_at: new Date().toISOString(),
                  user_name: user?.name || null,
                  media_kind: mediaType,
                  media_url: URL.createObjectURL(file), // preview local
                  media_mime: mediaMime,
                };
                setMessages(m => [...m, optimistic]);
                stickRef.current = true;

                const form = new FormData();
                form.append("file", file);

                try {
                  const { data } = await api.post("/chat/upload", form, {
                    headers: { "Content-Type": "multipart/form-data" }
                  });

                  const mediaUrl = data.mediaUrl || data.path;
                  const mediaSize = file.size;

                  await api.post(`/chat/threads/${selected.id}/send`, {
                    mediaUrl, mediaType, mediaMime, mediaSize,
                    client_msg_id, // ✅ clave para que WS haga upsert al optimista
                  });

                  // WS debe reemplazar el optimista con el real
                } catch (err) {
                  console.error(err);
                  alert("Error subiendo archivo");
                  // opcional: recargar mensajes para limpiar el optimista
                  fetchMessages(selected.id);
                } finally {
                  e.target.value = "";
                }
              }}
            />

            <button
              type="button"
              className="icon-btn"
              onClick={() => document.getElementById("fileInput").click()}
              title="Adjuntar archivo"
            >
              📎
            </button>
            <button className="btn btn-primary" disabled={!selected || !text.trim() || sending}>
              {sending ? "Enviando..." : "Enviar"}
            </button>
            <button
              type="button"
              onClick={openTemplateModal}
              className="px-3 py-2 bg-green-600 text-white rounded-lg mr-2"
            >
              Plantillas
            </button>
          </form>
        </div>
      </div>

      {/* Drawer Detalles */}
      <div className={`details-drawer ${detailsOpen ? "open" : ""}`}>
        <div className="drawer-header">
          <div className="drawer-title">
            Detalles
            {lead && (
              <span
                className="lead-chip"
                style={{ background: leadStatusColor(lead.status) }}
                title={`Lead #${lead.id} · ${lead.status || "—"}`}
              >
                {lead.status || "—"}
              </span>
            )}
          </div>
          <button className="icon-btn" onClick={() => setDetailsOpen(false)} title="Cerrar panel">
            <svg viewBox="0 0 24 24">
              <path d="M6 6l12 12M18 6l-12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="drawer-body">
          {!selected ? (
            <div className="muted">Sin selección</div>
          ) : (
            <>
              <div className="mini-card">
                <div className="mini-title">Lead {lead ? `#${lead.id}` : ""}</div>
                <div className="line">
                  <span className="muted small">
                    {lead ? `estado: ${lead.status || "—"}` : "Sin lead asociado"}
                  </span>
                </div>

                <div className="icon-row">
                  <button className="icon-action" onClick={onLeadStart} title="Iniciar / Trabajar" disabled={!selected}>
                    <svg viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" fill="currentColor" />
                    </svg>
                  </button>
                  <button
                    className="icon-action success"
                    onClick={() => onLeadClose("won")}
                    title="Cerrar como Ganado"
                    disabled={!selected || closingLead}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M12 3l2.5 5 5.5.8-4 3.9.9 5.6L12 16l-4.9 2.3.9-5.6-4-3.9 5.5-.8z" fill="currentColor" />
                    </svg>
                  </button>
                  <button
                    className="icon-action warn"
                    onClick={() => onLeadClose("lost")}
                    title="Cerrar como Perdido"
                    disabled={!selected || closingLead}
                  >
                    <svg viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                      <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                  <button
                    className="icon-action mute"
                    onClick={() => onLeadClose("closed")}
                    title="Cerrar"
                    disabled={!selected || closingLead}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M4 7h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7zm3-3h10v3H7V4z" fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="form">


                <label>
                  <span>Teléfono (detectado)</span>
                  <input className="input" value={selected?.phone || ""} readOnly />
                </label>

                <label>
                  <span>Teléfono corregido</span>
                  <input
                    className="input"
                    value={leadPhoneFix}
                    onChange={(e) => setLeadPhoneFix(e.target.value)}
                    placeholder="Ej: 5939XXXXXXXX"
                  />
                  <div className="muted small">
                    Este número se usará para el lead aunque WhatsApp detecte otro.
                  </div>
                </label>

                <div className="save">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveLeadPhoneFix}
                    disabled={!lead?.id || savingPhoneFix}
                  >
                    {savingPhoneFix ? "Guardando..." : "Guardar teléfono"}
                  </button>
                </div>

                <label>
                  <span>Número de contrato</span>
                  <input
                    className="input"
                    value={contractNumber}
                    onChange={(e) => setContractNumber(e.target.value)}
                    placeholder="Ej: CTR-2026-000123"
                  />
                  <div className="muted small">
                    Se guarda en el contacto (contacts.contract_number).
                  </div>
                </label>

                <div className="save">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveContractNumber}
                    disabled={!contact?.id || savingContract}
                  >
                    {savingContract ? "Guardando..." : "Guardar contrato"}
                  </button>
                </div>

                <label>
                  <span>Nombre</span>
                  <input
                    className="input"
                    value={meta.name}
                    onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                  />
                </label>

                <label>
                  <span>Tipo de cliente</span>
                  <select
                    className="input"
                    value={meta.customer_type || ""}
                    onChange={(e) => setMeta({ ...meta, customer_type: e.target.value })}
                  >
                    <option value=""></option>
                    <option value="nuevo">Nuevo</option>
                    <option value="recurrente">Recurrente</option>
                    <option value="vip">VIP</option>
                    <option value="moroso">Moroso</option>
                  </select>
                </label>

                <label>
                  <span>Presupuesto</span>
                  <input
                    className="input"
                    value={meta.budget}
                    onChange={(e) => setMeta({ ...meta, budget: e.target.value })}
                  />
                </label>

                <label>
                  <span>Estado</span>
                  <select
                    className="input"
                    value={meta.status || ""}
                    onChange={(e) => setMeta({ ...meta, status: e.target.value })}
                  >
                    <option value=""></option>
                    <option value="lead">Lead</option>
                    <option value="negociacion">Negociación</option>
                    <option value="cerrado">Cerrado</option>
                    <option value="moroso">Moroso</option>
                    <option value="seguimiento">Seguimiento</option>
                  </select>
                </label>

                <label>
                  <span>Notas</span>
                  <textarea
                    className="input"
                    rows={5}
                    value={meta.notes}
                    onChange={(e) => setMeta({ ...meta, notes: e.target.value })}
                  />
                </label>

                <label>
                  <span>Asignado a (Usuario real)</span>
                  <select
                    className="input"
                    value={meta.assigned_to || ""}
                    onChange={(e) =>
                      setMeta({
                        ...meta,
                        assigned_to: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                  >
                    <option value="">— Sin asignar —</option>
                    {accountUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} · {u.account_role}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="save">
                  <button className="btn btn-primary" onClick={onSaveMeta} disabled={!selected || savingMeta}>
                    {savingMeta ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bitácora Modal */}

      {bitOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-[720px] max-w-[92vw] rounded-2xl shadow-xl overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <div className="font-semibold text-lg  text-black">Bitácora / Novedad</div>
                <div className="text-sm text-gray-500  text-black">
                  Contrato: <b>{contractNumber || "—"}</b> · Usuario: <b>{user?.name || user?.username || "—"}</b>
                </div>
              </div>
              <button
                className="px-3 py-1.5 rounded-lg border  text-black hover:bg-gray-100"
                onClick={() => setBitOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              <textarea
                value={bitText}
                onChange={(e) => setBitText(e.target.value)}
                rows={9}
                placeholder="Escribe aquí la bitácora... (puedes pegar lo del chat)"
                className="w-full border rounded-xl px-3 py-2"
              />

              <div className="space-y-2">
                <div className="text-sm font-medium text-black">¿Contestar?</div>

                <div className="flex gap-4 flex-wrap">
                  <label className="flex items-center gap-2 text-black cursor-pointer">
                    <input
                      type="radio"
                      name="bitContestar"
                      value="1"
                      checked={bitContestar === "1"}
                      onChange={(e) => setBitContestar(e.target.value)}
                    />
                    Contesto
                  </label>

                  <label className="flex items-center gap-2 text-black cursor-pointer">
                    <input
                      type="radio"
                      name="bitContestar"
                      value="0"
                      checked={bitContestar === "0"}
                      onChange={(e) => setBitContestar(e.target.value)}
                    />
                    No contesto
                  </label>
                </div>
              </div>

              {bitErr ? <div className="text-red-600 text-sm">{bitErr}</div> : null}

              <div className="flex gap-2 justify-between flex-wrap">
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl border  bg-gray-100 hover:bg-gray-200  text-black"
                    onClick={() => setBitText(buildBitacoraFromChat(12))}
                    disabled={!selected}
                    title="Toma los últimos mensajes y los pega aquí"
                  >
                    Copiar del chat
                  </button>

                  <button
                    type="button"
                    className="px-3 py-2 rounded-xl border  bg-gray-100 hover:bg-gray-200  text-black"
                    onClick={() => setBitText("")}
                  >
                    Limpiar
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl border bg-gray-100 hover:bg-gray-200 text-black"
                    onClick={() => setBitOpen(false)}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white"
                    onClick={saveBitacora}
                    disabled={bitSaving}
                  >
                    {bitSaving ? "Guardando..." : "Guardar bitácora"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Modal Plantillas */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white w-[500px] max-h-[80vh] rounded-xl shadow-lg overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-semibold text-lg">Plantillas</h2>
              <button onClick={() => setShowTemplates(false)}>✕</button>
            </div>

            <div className="p-3 border-b">
              <input
                type="text"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchTemplates()}
                placeholder="Buscar plantilla..."
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loadingTemplates && <p>Cargando...</p>}

              {!loadingTemplates && templates.length === 0 && (
                <p className="text-gray-500 text-center">No hay plantillas</p>
              )}

              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => onSendTemplate(tpl)}
                >
                  <p className="font-bold">{tpl.title}</p>
                  <p className="text-gray-700 text-sm whitespace-pre-line mt-1">
                    {tpl.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <BitacoraHistoryModal
        open={bitHistoryOpen}
        onClose={() => setBitHistoryOpen(false)}
        contrato={contractNumber}
        usuarioLabel={user?.name || user?.username || ""}
      />

      <DocumentosClienteModal
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        contratoDefault={contractNumber || ""}
        clienteDefault=""
      />

      <SiniestralidadModal
        open={siniesOpen}
        onClose={() => setSiniesOpen(false)}
        contrato={contractNumber}
      />

    </div>
  );
}

/* -------------------------------------------------------
   CSS
-------------------------------------------------------- */
const css = `
:root{
  --border: rgba(255,255,255,.08);
  --bg: #0b1220;
  --panel: #0f172a;
  --text: #e5e7eb;
  --muted: #94a3b8;
  --brand: #3b82f6;
  --brand-2: #2563eb;
  --green: #22c55e; --amber:#f59e0b; --red:#ef4444; --blue:#60a5fa; --slate:#94a3b8;
}

.chat-root{ height: calc(100vh - 80px); background: var(--bg); color: var(--text); display:grid; grid-template-rows:auto 1fr; }
.topbar{
  display:flex; align-items:center; gap:10px; padding:10px 12px; border-bottom:1px solid var(--border); background: rgba(255,255,255,.02);
}
.left-actions{ display:flex; align-items:center; gap:8px; }
.btn.back{ background: rgba(255,255,255,.06); border:1px solid var(--border); color:#eaf2ff; padding:6px 12px; border-radius:10px; cursor:pointer; }
.btn.back:hover{ background: rgba(59,130,246,.15); border-color: rgba(59,130,246,.35); }

.search.compact{ position:relative; flex:1; max-width:520px; }
.search .icon{ position:absolute; left:10px; top:50%; transform:translateY(-50%); width:16px; height:16px; color:var(--muted); }
.input-search{ padding-left:32px !important; }
.input{ width:100%; background: rgba(255,255,255,.04); border: 1px solid var(--border); color: var(--text); padding: 8px 12px; border-radius: 10px; }
.input:focus { outline: none; border-color: rgba(59,130,246,.45); box-shadow: 0 0 0 3px rgba(59,130,246,.15); }

.right-info{ display:flex; align-items:center; gap:10px; margin-left:auto; }
.unread{ color:#cfe1ff; background: rgba(59,130,246,.15); border:1px solid rgba(59,130,246,.35); padding:4px 10px; border-radius:999px; font-size:12px; }
.unread b{ color:#fff; }

.lead-counters{ display:flex; gap:6px; align-items:center; }
.pill{ border:1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.04); padding:4px 8px; border-radius:999px; color:#cfd8ef; font-size:12px; }
.pill.open{ border-color:#60a5fa33; }
.pill.working{ border-color:#f59e0b33; }
.pill.won{ border-color:#22c55e33; }
.pill.lost{ border-color:#ef444433; }
.pill.closed{ border-color:#94a3b833; }
.new24{ display:flex; align-items:center; gap:6px; border:1px solid #3b82f6; color:#cfe1ff; padding:4px 8px; border-radius:999px; background:#1e3a8a; font-weight:700; }
.new24 svg{ width:16px; height:16px; }
.new24.pulse{ box-shadow:0 0 0 0 rgba(59,130,246,.6); animation:pulse 1.6s infinite; }
@keyframes pulse{ 70%{ box-shadow:0 0 0 10px rgba(59,130,246,0);} 100%{ box-shadow:0 0 0 0 rgba(59,130,246,0);} }

.chat-grid{
  display:grid; grid-template-columns: 320px 1fr; gap:12px; height:100%; overflow:hidden; padding:10px;
}
.chat-grid.left-collapsed{ grid-template-columns: 1fr; }

.card { background: var(--panel); border-radius: 14px; border: 1px solid var(--border); overflow: hidden; }
.left-pane, .center-pane { display: grid; grid-template-rows: 1fr auto; }
.section { padding: 12px; }

.threads-scroll{ overflow-y:auto; }

.thread-item { padding: 10px 12px; border-bottom: 1px solid var(--border); cursor: pointer; transition: background .15s ease; }
.thread-item:hover { background: rgba(255,255,255,.03); }
.thread-item.selected { background: rgba(59,130,246,.12); border-left: 2px solid var(--brand); }

.thread-top { display: grid; grid-template-columns: 36px 1fr auto; align-items: center; gap: 10px; }
.thread-avatar { width: 32px; height: 32px; border-radius: 999px; background: linear-gradient(180deg, #93c5fd, #bfdbfe); color: #0b1b3a; display: grid; place-items: center; font-weight: 800; font-size: 12px; }
.thread-meta { display: grid; gap: 2px; }
.thread-name { font-weight: 600; color: #e7eaf3; }
.thread-sub { font-size: 11px; color: var(--muted); display: flex; align-items: center; gap: 6px; }
.tag { background: rgba(59,130,246,.18); color: #cfe1ff; padding: 2px 6px; border-radius: 999px; font-size: 10px; border: 1px solid rgba(59,130,246,.35); }
.sep { opacity: .5; }
.badge { font-size: 12px; background: #ef4444; color: #fff; border-radius: 999px; padding: 2px 7px; font-weight: 700; }

.thread-bottom { display: flex; justify-content: space-between; gap: 8px; margin-top: 6px; font-size: 12px; color: var(--muted); }
.ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.muted { color: var(--muted); }
.small { font-size: 12px; }

.header-row { border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.header-left .title { font-weight: 700; font-size: 16px; color: #fff; }
.header-actions { display: flex; gap: 8px; align-items: center; }

.icon-btn { width: 34px; height: 34px; border-radius: 10px; border: 1px solid var(--border); background: rgba(255,255,255,.04); display: grid; place-items: center; color: var(--muted); cursor: pointer; }
.icon-btn:hover { color: #dbe5ff; background: rgba(59,130,246,.15); border-color: rgba(59,130,246,.35); }
.icon-btn.active { color: #dbe5ff; background: rgba(59,130,246,.18); border-color: rgba(59,130,246,.4); }
.icon-btn svg { width: 18px; height: 18px; }

.messages { padding: 12px; overflow-y: auto; background: #0b1220; position: relative; }
.date-sep { position: sticky; top: 6px; width: max-content; margin: 10px auto; padding: 2px 10px; border-radius: 999px; background: #1f2937; color: #cbd5e1; font-size: 12px; border: 1px solid #273446; }

.msg-row { display: flex; margin: 8px 0; }
.msg-row.right { justify-content: flex-end; }
.msg-row.left { justify-content: flex-start; }

.bubble { max-width: 72%; padding: 10px 12px; border-radius: 14px; border: 1px solid #1e293b; background: #111827; box-shadow: 0 1px 0 rgba(0,0,0,.03); color: #e5e7eb; }
.bubble.out { background: #0b2a4a; border-color: #1f3a5d; }
.bubble-text { white-space: pre-wrap; color: #e5e7eb; }
.msg-meta { font-size: 11px; color: #9ca3af; margin-top: 4px; }

.scroll-bottom { position: absolute; right: 14px; bottom: 14px; padding: 6px 10px; border-radius: 999px; border: 1px solid #273446; background: #0f172a; color: #cbd5e1; box-shadow: 0 6px 20px rgba(0,0,0,.25); }

.btn { background: rgba(255,255,255,.05); border: 1px solid var(--border); color: var(--text); border-radius: 10px; padding: 8px 12px; cursor: pointer; }
.btn:hover { background: rgba(255,255,255,.08); }
.btn-primary { background: var(--brand); border-color: var(--brand-2); }
.btn-primary:hover { background: var(--brand-2); }

.input-bar { display: grid; grid-template-columns: 1fr auto; gap: 8px; border-top: 1px solid var(--border); }

/* Drawer */
.details-drawer { position: fixed; top: 72px; right: -420px; width: 380px; height: calc(100vh - 84px); background: var(--panel); border-left: 1px solid var(--border); transition: right .25s ease; z-index: 30; display: grid; grid-template-rows: auto 1fr; border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
.details-drawer.open { right: 8px; }

.drawer-header { display: flex; align-items: center; justify-content: space-between; padding: 12px; border-bottom: 1px solid var(--border); }
.drawer-title { font-weight: 700; display: flex; align-items: center; gap: 8px; }
.lead-chip { color: #0b1220; font-weight: 700; padding: 2px 8px; border-radius: 999px; font-size: 11px; text-transform: uppercase; letter-spacing: .3px; }

.drawer-body { overflow-y: auto; padding: 12px; display: grid; gap: 12px; }
.mini-card { padding: 12px; background: rgba(255,255,255,.04); border-radius: 10px; border: 1px solid var(--border); }
.mini-title { font-weight: 600; margin-bottom: 8px; }
.line { margin-bottom: 8px; }

.icon-row { display: flex; gap: 8px; }
.icon-action { width: 40px; height: 40px; border-radius: 12px; border: 1px solid var(--border); background: rgba(255,255,255,.04); display: grid; place-items: center; color: var(--muted); cursor: pointer; }
.icon-action:hover { background: rgba(59,130,246,.15); color: #e2ebff; border-color: rgba(59,130,246,.35); }
.icon-action.success { color: #22c55e; }
.icon-action.warn { color: #ef4444; }
.icon-action.mute { color: #94a3b8; }
.icon-action svg { width: 18px; height: 18px; }

.form { display: grid; gap: 10px; }
.form label { display: grid; gap: 6px; font-size: 12px; color: var(--muted); }
.save { display: flex; justify-content: flex-end; }

/* Responsive */
@media (max-width: 1200px) {
  .chat-grid { grid-template-columns: 1fr; }
}

/* --- HEADER COMPACT / FIX ICON SIZING --- */
.header-row{
  padding: 8px 12px;
  max-height: 52px;
}
.header-left .title{ font-size: 15px; }
.header-actions{ display:flex; gap:8px; align-items:center; }

/* Botones de icono del header pequeños y consistentes */
.header-actions .icon-btn{
  width: 32px; height: 32px; border-radius: 8px;
}
.header-actions .icon-btn > svg{
  width: 18px !important;
  height: 18px !important;
  display: block;
  flex: none;
}

/* En caso de que tengas una regla global svg { width:100% } */
.center-pane .header-row svg{
  width: auto; height: auto;
}

/* Select de canal compacto */
.header-actions .select-wrap.small{ min-width: 220px; }
.header-actions .select{
  height: 32px;
  padding: 0 28px 0 10px;
  line-height: 32px;
}
.header-actions .select-wrap .caret{
  width: 14px; height: 14px; right: 8px;
}

/* Badges y contadores compactos */
.unread{
  padding: 3px 8px; font-size: 11px;
}
.lead-counters .pill{
  padding: 3px 8px; font-size: 11px;
}
.lead-counters .new24{
  padding: 3px 8px; font-size: 11px;
}

/* Toggle mini filtro como segmented control */
.view-filter{
  display:flex; gap:6px; align-items:center;
  background: rgba(255,255,255,.04);
  border:1px solid var(--border);
  padding:3px;
  border-radius:999px;
}
.vf-pill{
  background: transparent;
  border:0;
  color:#cfe1ff;
  padding:6px 12px;
  border-radius:999px;
  font-size:12px;
  cursor:pointer;
  display:flex; align-items:center; gap:6px;
}
.vf-pill .count{
  background: rgba(59,130,246,.25);
  border: 1px solid rgba(59,130,246,.35);
  color:#eaf2ff;
  font-weight:700;
  font-size:11px;
  padding:1px 6px;
  border-radius:999px;
}
.vf-pill.active{
  background: rgba(59,130,246,.18);
  border:1px solid rgba(59,130,246,.35);
}

/* versión mini de los contadores de leads en el header derecho */
.lead-counters.mini { display:flex; gap:6px; align-items:center; }
.lead-counters.mini .pill{ padding:3px 8px; font-size:11px; }
.lead-counters.mini .new24{ padding:3px 8px; font-size:11px; }

.thread-right{ display:flex; align-items:center; gap:8px; justify-self:end; }
.status-pill{
  font-size:10px; text-transform:uppercase; font-weight:800;
  padding:2px 8px; border-radius:999px; border:1px solid transparent; letter-spacing:.3px;
}
.status-pill.open{ background: rgba(59,130,246,.18); border-color: rgba(59,130,246,.35); color:#cfe1ff; }
.status-pill.working{ background: rgba(245,158,11,.18); border-color: rgba(245,158,11,.35); color:#fde68a; }

.bubble-body{ display:grid; gap:6px; }
.bubble-img{ max-width: 280px; width:100%; border-radius:8px; display:block; }
.bubble-audio{ width:260px; display:block; }

.btn-bitacora{
  display:flex;
  align-items:center;
  gap:8px;
  padding: 8px 12px;
  border-radius: 12px;
  font-weight: 800;
  letter-spacing: .2px;

  background: rgba(34,197,94,.18);
  border: 1px solid rgba(34,197,94,.45);
  color: #dcfce7;

  box-shadow: 0 10px 30px rgba(34,197,94,.10);
}

.btn-bitacora:hover{
  background: rgba(34,197,94,.26);
  border-color: rgba(34,197,94,.65);
  transform: translateY(-1px);
}
  .tag.contract{
  background: rgba(245,158,11,.16);
  border-color: rgba(245,158,11,.38);
  color: #ffedd5;
}

.title-row{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
}

.contract-pill{
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .4px;
  text-transform: uppercase;

  padding: 4px 10px;
  border-radius: 999px;

  background: rgba(245,158,11,.16);
  border: 1px solid rgba(245,158,11,.38);
  color: #ffedd5;

  white-space: nowrap;
  flex: none;
}

`;
